import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid,
  TextField, Alert, CircularProgress, Chip, List,
  ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl,
  InputLabel, Divider, Avatar
} from '@mui/material';
import PersonAddIcon   from '@mui/icons-material/PersonAdd';
import DeleteIcon      from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon      from '@mui/icons-material/People';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { sitesAPI, enrollmentAPI } from '../services/api';
import api from '../services/api';

export default function Enrollment() {
  const [sites,       setSites]       = useState([]);
  const [siteId,      setSiteId]      = useState('');
  const [employees,   setEmployees]   = useState([]);
  const [devices,     setDevices]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [enrolling,   setEnrolling]   = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [enrollmentActive, setEnrollmentActive] = useState(false);
  const [enrollmentStartedAt, setEnrollmentStartedAt] = useState(null);
  const [sensorReady, setSensorReady]     = useState(false);
  const [countdown,   setCountdown]       = useState(300); // 5 min en secondes
  const [form, setForm] = useState({ nom: '', prenom: '', poste: '',
    start_time: '09:00', end_time: '18:00' });

  useEffect(() => {
    sitesAPI.list().then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!siteId) return;
    loadEmployees();
  }, [siteId]);

  // Poll toutes les 10s + countdown 5 min pendant la session d'enrollment
  useEffect(() => {
    if (!enrollmentActive || !siteId) return;
    const startTs = Date.now();
    setEnrollmentStartedAt(startTs);
    setSensorReady(false);
    setCountdown(300);

    const poll = setInterval(async () => {
      try {
        const r = await enrollmentAPI.start({ site_id: siteId });
        setDevices(r.data.devices || []);
      } catch {}
    }, 10000);

    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTs) / 1000);
      const remaining = Math.max(0, 300 - elapsed);
      setCountdown(remaining);
      if (remaining === 0) setSensorReady(true);
    }, 1000);

    return () => { clearInterval(poll); clearInterval(tick); };
  }, [enrollmentActive, siteId]);

  // Desactiver enrollment_mode quand on quitte la page
  useEffect(() => {
    return () => {
      if (enrollmentActive && siteId)
        api.post('/v1/enrollment/stop', { site_id: siteId }).catch(() => {});
    };
  }, [enrollmentActive, siteId]);

  const loadEmployees = async () => {
    if (!siteId) return;
    try {
      const r = await api.get(`/v1/employees/${siteId}`);
      setEmployees(r.data.employees || []);
    } catch {}
  };

  const startEnrollment = async () => {
    setEnrolling(true); setError(''); setDevices([]);
    try {
      const r = await enrollmentAPI.start({ site_id: siteId });
      setDevices(r.data.devices || []);
      setEnrollmentActive(true);
      if (r.data.devices?.length === 0) {
        setError('Aucun device detecte proche. Demandez a l\'employe d\'approcher son telephone a moins de 30cm du capteur avec le WiFi active.');
      }
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur demarrage enrollment');
    } finally { setEnrolling(false); }
  };

  const stopEnrollment = () => {
    setEnrollmentActive(false);
    setSensorReady(false);
    setCountdown(300);
    setDevices([]);
    if (siteId) api.post('/v1/enrollment/stop', { site_id: siteId }).catch(() => {});
  };

  const confirmDevice = async () => {
    if (!selectedDevice || !form.nom || !form.prenom) return;
    setLoading(true);
    try {
      await enrollmentAPI.confirm({
        site_id:     siteId,
        ie_fp_hash:  selectedDevice.ie_fp,
        nom:         form.nom,
        prenom:      form.prenom,
        poste:       form.poste,
        start_time:  form.start_time,
        end_time:    form.end_time,
      });
      setSuccess(`${form.prenom} ${form.nom} enregistre avec succes`);
      setDialogOpen(false);
      stopEnrollment();
      setForm({ nom: '', prenom: '', poste: '', start_time: '09:00', end_time: '18:00' });
      loadEmployees();
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur confirmation');
    } finally { setLoading(false); }
  };

  const deviceCount = devices.length;
  const deviceStatus = deviceCount === 0 ? 'waiting'
    : deviceCount === 1 ? 'ready' : 'multiple';

  const statusConfig = {
    waiting:  { color: '#FF9800', label: 'En attente', msg: 'Demandez a l\'employe d\'approcher son telephone' },
    ready:    { color: '#4CAF50', label: '1 device detecte', msg: 'Parfait ! Confirmez le device de l\'employe' },
    multiple: { color: '#F44336', label: `${deviceCount} devices detectes`, msg: 'Demandez aux autres de s\'eloigner a plus de 3m' },
  }[deviceStatus];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="#1F4E79" mb={1}>
        Enrollment employes
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Enregistrez les appareils des employes pour les exclure des statistiques visiteurs
      </Typography>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Panel gauche — Demarrer enrollment */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PersonAddIcon color="primary" />
                <Typography fontWeight={600}>Nouvel enrollment</Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Boutique</InputLabel>
                <Select value={siteId} label="Boutique"
                  onChange={e => { setSiteId(e.target.value); setDevices([]); }}>
                  {sites.map(s => <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>

              {!enrollmentActive ? (
                <Button fullWidth variant="contained" size="large"
                  onClick={startEnrollment}
                  disabled={!siteId || enrolling}
                  startIcon={enrolling ? <CircularProgress size={18} color="inherit" /> : <PhoneAndroidIcon />}
                  sx={{ mb: 3, bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}>
                  Detecter les devices proches
                </Button>
              ) : (
                <Button fullWidth variant="outlined" size="large"
                  onClick={stopEnrollment}
                  sx={{ mb: 3, borderColor: '#E53935', color: '#E53935', '&:hover': { bgcolor: '#FFEBEE' } }}>
                  Arreter la detection
                </Button>
              )}

              {/* Jauge reception capteur */}
              {enrollmentActive && (
                <Box sx={{ mb: 3, p: 2, borderRadius: 2,
                  bgcolor: sensorReady ? '#E8F5E9' : '#E3F2FD',
                  border: `1px solid ${sensorReady ? '#A5D6A7' : '#90CAF9'}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}
                      color={sensorReady ? '#2E7D32' : '#1565C0'}>
                      {sensorReady
                        ? '✓ Capteur en mode rapide (30s)'
                        : 'Passage en mode rapide...'}
                    </Typography>
                    {!sensorReady && (
                      <Typography variant="caption" color="text.secondary">
                        {Math.floor(countdown / 60)}m {String(countdown % 60).padStart(2, '0')}s
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: sensorReady ? '#A5D6A7' : '#BBDEFB', overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', borderRadius: 3,
                      bgcolor: sensorReady ? '#4CAF50' : '#1976D2',
                      width: `${sensorReady ? 100 : Math.round((1 - countdown / 300) * 100)}%`,
                      transition: 'width 1s linear',
                    }} />
                  </Box>
                  {!sensorReady && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Le capteur adapte sa cadence au prochain heartbeat
                    </Typography>
                  )}
                </Box>
              )}

              {/* Statut devices */}
              {(devices.length > 0 || enrolling) && (
                <Box sx={{ p: 2, borderRadius: 2,
                  bgcolor: `${statusConfig.color}10`,
                  border: `1px solid ${statusConfig.color}30` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%',
                      bgcolor: statusConfig.color,
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 }
                      }}} />
                    <Typography fontWeight={600} color={statusConfig.color}>
                      {statusConfig.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {statusConfig.msg}
                  </Typography>
                </Box>
              )}

              {/* Liste devices */}
              {devices.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {devices.map((device, i) => (
                    <Box key={i} sx={{ p: 2, borderRadius: 2, border: '1px solid #E0E0E0',
                      mb: 1, display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: deviceCount === 1 ? '#F1F8E9' : 'white' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {device.brand ? device.brand : `Device ${i + 1}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          RSSI: {device.rssi_max} dBm
                          {device.vendor_oui && !device.brand && ` · OUI: ${device.vendor_oui}`}
                        </Typography>
                      </Box>
                      {deviceCount === 1 && (
                        <Button size="small" variant="contained"
                          onClick={() => { setSelectedDevice(device); setDialogOpen(true); }}
                          sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}>
                          Confirmer
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Instructions legales */}
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            mt: 2, bgcolor: '#FFF3E0' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" fontWeight={600} color="#E65100" mb={1}>
                Prerequis legaux
              </Typography>
              {[
                'Clause enrollment signee dans le contrat REDIA',
                'Consentement ecrit de l\'employe',
                'Note information affichee en salle de pause',
              ].map((t, i) => (
                <Typography key={i} variant="caption" display="block" color="#E65100" mb={0.3}>
                  ☐ {t}
                </Typography>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Panel droit — Liste employes */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PeopleIcon color="primary" />
                <Typography fontWeight={600}>Employes enregistres</Typography>
                <Chip label={employees.length} size="small"
                  sx={{ ml: 'auto', bgcolor: '#E3F2FD', color: '#1F4E79' }} />
              </Box>

              {employees.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <PeopleIcon sx={{ fontSize: 48, color: '#E0E0E0', mb: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Aucun employe enregistre pour ce site
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {employees.map((emp, i) => (
                    <React.Fragment key={i}>
                      <ListItem>
                        <Avatar sx={{ mr: 2, bgcolor: '#1F4E7920', color: '#1F4E79',
                          width: 36, height: 36, fontSize: '0.85rem' }}>
                          {emp.prenom?.[0]}{emp.nom?.[0]}
                        </Avatar>
                        <ListItemText
                          primary={`${emp.prenom} ${emp.nom}`}
                          secondary={emp.poste}
                        />
                        <ListItemSecondaryAction>
                          <Chip label="Actif" size="small"
                            sx={{ bgcolor: '#E8F5E9', color: '#4CAF50', mr: 1 }} />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {i < employees.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog confirmation */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmer l'employe</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            {selectedDevice?.brand
              ? <><strong>{selectedDevice.brand}</strong> detecte — RSSI: {selectedDevice?.rssi_max} dBm</>
              : <>Device detecte — RSSI: {selectedDevice?.rssi_max} dBm</>
            }
          </Alert>
          <Grid container spacing={2}>
            {[
              { label: 'Nom', key: 'nom', required: true },
              { label: 'Prenom', key: 'prenom', required: true },
              { label: 'Poste', key: 'poste', required: false },
              { label: 'Heure debut', key: 'start_time', required: false, placeholder: '09:00' },
              { label: 'Heure fin', key: 'end_time', required: false, placeholder: '18:00' },
            ].map(field => (
              <Grid item xs={12} sm={field.key.includes('time') ? 6 : 12} key={field.key}>
                <TextField fullWidth label={field.label} required={field.required}
                  value={form[field.key]}
                  onChange={e => setForm(prev => ({...prev, [field.key]: e.target.value}))}
                  placeholder={field.placeholder} />
              </Grid>
            ))}
          </Grid>
          <Alert severity="warning" sx={{ mt: 2, fontSize: '0.8rem' }}>
            En confirmant, vous attestez que le consentement ecrit de cet employe a ete obtenu.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={confirmDevice}
            disabled={loading || !form.nom || !form.prenom}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}>
            Enregistrer l'employe
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
